import { useEffect, useState, useCallback } from 'react';
import authService from '@/services/authService';

type Theme = 'dark' | 'light' | 'system';

export function useTheme() {
  const [theme, setTheme] = useState<Theme>('system');
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Apply theme to DOM
  const applyTheme = useCallback((themeValue: Theme) => {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    if (themeValue === 'light') {
      root.classList.add('light');
    } else {
      // For 'dark' or 'system', we default to 'dark'
      // This ensures 'dark:' utilities work and variables use the :root (dark) definition
      root.classList.add('dark');
    }
  }, []);

  // Initialize theme on mount
  useEffect(() => {
    const initializeTheme = async () => {
      setIsLoading(true);
      
      try {
        // Safety: Wrap in try-catch with timeout protection
        const initPromise = (async () => {
          // Check if user is authenticated
          const isAuthenticated = authService.isAuthenticated();
          
          if (isAuthenticated) {
            // Load theme preference from database with fallback
            const userTheme = await authService.getThemePreference();
            // Safety: Validate theme value before applying
            if (userTheme === 'light' || userTheme === 'dark' || userTheme === 'system') {
              setTheme(userTheme);
              applyTheme(userTheme);
            } else {
              // Invalid theme, fallback to system
              console.warn('Invalid theme value, falling back to system:', userTheme);
              setTheme('system');
              applyTheme('system');
            }
          } else {
            // Non-logged-in users always use system mode
            setTheme('system');
            applyTheme('system');
          }
        })();

        // Add timeout protection (10 seconds max)
        const timeoutPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            console.warn('Theme initialization timeout, falling back to system');
            setTheme('system');
            applyTheme('system');
            resolve();
          }, 10000);
        });

        await Promise.race([initPromise, timeoutPromise]);
      } catch (error) {
        // Safety: Catch all errors and fallback to system mode
        console.error('Error initializing theme (fallback to system):', error);
        setTheme('system');
        applyTheme('system');
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeTheme();
  }, [applyTheme]);

  // Apply theme when it changes
  useEffect(() => {
    if (isInitialized) {
      applyTheme(theme);
    }
  }, [theme, isInitialized, applyTheme]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    // Note: We are now treating "System" as "Dark" default, ignoring OS preferences for now per user request.
    // If we want to restore OS auto-detection, uncomment logic below.
    /*
    if (theme !== 'system' || !isInitialized) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('system');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
    */
  }, [theme, isInitialized, applyTheme]);

  // Listen for auth state changes - reload theme when user logs in
  useEffect(() => {
    if (!isInitialized) return;

    const checkAuthAndLoadTheme = async () => {
      const isAuthenticated = authService.isAuthenticated();
      
      if (isAuthenticated) {
        // User is logged in, ensure we have their theme preference
        try {
          const userTheme = await authService.getThemePreference();
          if (userTheme !== theme) {
            setTheme(userTheme);
          }
        } catch (error) {
          console.error('Error loading theme after login:', error);
        }
      } else {
        // User logged out, reset to system mode
        if (theme !== 'system') {
          setTheme('system');
        }
      }
    };

    // Check immediately and then periodically
    checkAuthAndLoadTheme();
    const interval = setInterval(checkAuthAndLoadTheme, 2000);
    return () => clearInterval(interval);
  }, [isInitialized, theme]);

  const setThemeValue = async (newTheme: Theme) => {
    // Safety: Validate theme value
    if (newTheme !== 'light' && newTheme !== 'dark' && newTheme !== 'system') {
      console.warn('Invalid theme value, ignoring:', newTheme);
      return;
    }

    // Apply theme to UI immediately (optimistic update)
    setTheme(newTheme);
    applyTheme(newTheme);

    // Save to database in background (non-blocking)
    // This is a fire-and-forget operation - UI is already updated
    const isAuthenticated = authService.isAuthenticated();
    if (isAuthenticated) {
      // Don't await - let it run in background
      authService.updateThemePreference(newTheme).catch((error) => {
        // Silently log error - UI is already updated, so this is non-critical
        console.error('Background theme preference update failed (non-critical):', error);
      });
    }
    // Non-logged-in users: theme is already applied, no need to save
  };

  return {
    theme,
    setTheme: setThemeValue,
    isLoading,
    isInitialized,
  };
}
